import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import moment from 'moment';
import { profileAction } from "@redux/modules";
import showErrMessage from "@components/ShowErrMessage";
import showMessage from "@components/ShowMessage";

const Messages = () => {
  const dispatch = useDispatch();
  const loginSettings = useSelector((store) => store.auth.loginSettings);
  const subjectList = useSelector((store) => store.profile.subjectList);

  // Upload Ref
  const uploadInput = useRef();
  const [uploadInputValidation, setUploadInputValidation] = useState(false);

  // Upload Refs
  const uploadInputRefs = useRef([]);

  const [pageState, setPageState] = useState({
    newMessageLengthLeft: 1000,
    responseMessages: [],
    isLoading: false,
    message: "",
    messageSubject: "",
    messageMain: "",
  });

  const profileDetail = useSelector((store) => store.profile.profileDetail);
  const [employeeName, setEmployeeName] = useState("");

  const messageSchema = yup.object({
    messageSubject: yup.string().required("Lütfen bir konu seçiniz!"),
    message: yup
      .string()
      .required("Lütfen bir mesaj giriniz!")
      .min(10, "Mesajınız en az 10 karakter olmalıdır!")
      .max(1000, "Mesajınız en fazla 1000 karakter olmalıdır!")
      .matches(
        /^[a-zA-ZğüşıöçĞÜŞİÖÇ0-9\s.,!?()-]+$/,
        "Mesajınız sadece harf, rakam ve temel noktalama işaretleri içerebilir!"
      ),
  });

  const replySchema = yup.object({
    messageMain: yup
      .string()
      .required("Lütfen bir mesaj giriniz!")
      .min(10, "Mesajınız en az 10 karakter olmalıdır!")
      .max(1000, "Mesajınız en fazla 1000 karakter olmalıdır!")
      .matches(
        /^[a-zA-ZğüşıöçĞÜŞİÖÇ0-9\s.,!?()-]+$/,
        "Mesajınız sadece harf, rakam ve temel noktalama işaretleri içerebilir!"
      ),
  });

  useEffect(() => {
    dispatch(profileAction.getSubjectList());
    dispatch(profileAction.getLoginUserDetail());

    return () => {};
  }, [dispatch]);

  useEffect(() => {
    if (profileDetail) {
      setEmployeeName(
        profileDetail?.employeeDTO?.name +
          " " +
          profileDetail?.employeeDTO?.surname
      );
    }
  }, [profileDetail]);

  useEffect(() => {
    if (subjectList) {
      setPageState({
        ...pageState,
        responseMessages: subjectList,
      });
    }

    return () => {};
  }, [subjectList]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const maxSize = 4 * 1024 * 1024;

    if (file?.size > maxSize) {
      setUploadInputValidation(true);
    } else {
      setUploadInputValidation(false);
    }
  };

  const handleNewMessageSubmit = async (values, { resetForm }) => {
    if (uploadInputValidation) {
      showErrMessage("Dosya boyutu çok büyük!");
      return;
    }

    setPageState((prevState) => ({ ...prevState, isLoading: true }));

    try {
      await dispatch(
        profileAction.sendMessage(
          values.message,
          values.messageSubject,
          uploadInput.current.files[0]
        )
      );
      showMessage("Mesaj Gönderildi!");
      resetForm();
      if (uploadInput.current) {
        uploadInput.current.value = "";
      }
      dispatch(profileAction.getSubjectList());
    } catch (err) {
      showErrMessage("Mesaj Gönderilemedi. Bir Hata Oluştu!");
    } finally {
      setPageState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const handleNewMessageByIdSubmit = async (
    values,
    { resetForm },
    contactSubjectId,
    index
  ) => {
    setPageState((prevState) => ({ ...prevState, isLoading: true }));

    try {
      const file = uploadInputRefs.current[index].files[0];
      await dispatch(
        profileAction.sendMessageById(
          contactSubjectId,
          values.messageMain,
          file
        )
      );
      showMessage("Mesaj Gönderildi!");
      resetForm();
      if (uploadInputRefs.current[index]) {
        uploadInputRefs.current[index].value = "";
      }
      dispatch(profileAction.getSubjectList());
    } catch (err) {
      showErrMessage("Mesaj Gönderilemedi. Bir Hata Oluştu!");
    } finally {
      setPageState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const handleNewMessageChange = (event) => {
    const { value, name } = event.target;
    if (name === "message") {
      setPageState((prevState) => ({
        ...prevState,
        newMessageLengthLeft: 1000 - value.length,
        message: value,
      }));
    } else if (name === "messageSubject") {
      setPageState((prevState) => ({
        ...prevState,
        messageSubject: value,
      }));
    }
  };

  const handleMessageClick = (event, id) => {
    dispatch(profileAction.getSubjectById(id));
    setPageState((prevState) => ({
      ...prevState,
      messageMain: "",
    }));
  };

  return (
    <React.Fragment>
      <h2 className="mb-sm-6 mb-4">Mesajlarım</h2>

      <h5 className="text-blue mb-sm-5 mb-4">Bize Ulaşın</h5>
      <Formik
        validationSchema={messageSchema}
        onSubmit={handleNewMessageSubmit}
        initialValues={{
          messageSubject: "",
          message: "",
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form className="mb-sm-6 mb-4" onSubmit={handleSubmit}>
            <div className="row g-5">
              <div className="col-md-6 order-md-0 order-1">
                <div className="form-floating mb-4">
                  <select
                    className={`form-select ${
                      errors.messageSubject && touched.messageSubject
                        ? "is-invalid"
                        : ""
                    }`}
                    name="messageSubject"
                    value={values.messageSubject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Seçiniz</option>
                    <option value="Genel">Genel</option>
                    <option value="Şikayet/Dükkan">Şikayet/Dükkan</option>
                    <option value="Şikayet/Yarışmalar">
                      Şikayet/Yarışmalar
                    </option>
                    <option value="Şikayet/Puan Kazanımı ">
                      Şikayet/Puan Kazanımı
                    </option>
                    <option value="Şikayet/Diğer">Şikayet/Diğer</option>
                    <option value="Öneri/Dükkan">Öneri/Dükkan</option>
                    <option value="Öneri/Yarışmalar">Öneri/Yarışmalar</option>
                    <option value="Öneri/Puan Kazanımı">
                      Öneri/Puan Kazanımı
                    </option>
                    <option value="Öneri/Diğer">Öneri/Diğer</option>
                    <option value="Bilgi">Bilgi</option>
                    <option value="Memnuniyet">Memnuniyet</option>
                  </select>
                  <label>Konu</label>
                  {errors.messageSubject && touched.messageSubject && (
                    <div className="invalid-feedback">
                      {errors.messageSubject}
                    </div>
                  )}
                </div>
                <div className="mb-2">
                  <textarea
                    className={`form-control ${
                      errors.message && touched.message ? "is-invalid" : ""
                    }`}
                    placeholder="Mesajınız"
                    style={{ height: "100px" }}
                    name="message"
                    maxLength="1000"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.message}
                  ></textarea>
                  {errors.message && touched.message && (
                    <div className="invalid-feedback">{errors.message}</div>
                  )}
                </div>
                <span
                  className={`small d-block text-end mb-4${
                    1000 - values.message.length <= 100 ? " text-red" : ""
                  }`}
                >
                  {1000 - values.message.length} karakter kaldı.
                </span>
                <div className="mb-4">
                  <label className="form-label">Dosya / Fotoğraf Ekleyin</label>
                  <input
                    className="form-control"
                    type="file"
                    name="imgSrc"
                    ref={uploadInput}
                    onChange={(event) => handleFileChange(event)}
                  />
                  {uploadInputValidation && (
                    <div className="mt-3 text-red">
                      Dosya boyutu maksimum 4MB olabilir!
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn btn-blue py-sm-3 py-2 px-sm-4 px-3 w-100"
                  disabled={pageState.isLoading || uploadInputValidation}
                >
                  {pageState.isLoading ? (
                    <span className="spinner-border spinner-border-sm me-3"></span>
                  ) : null}
                  Gönder
                </button>
              </div>
              <div className="col-md-6 order-md-1 order-0">
                <p className="mb-0 fw-light lh-lg">
                  Sizi dinliyoruz. Her türlü görüş, öneri ve isteklerinizi <span className="text-blue">Bize
                  Ulaşın</span> bölümü aracılığıyla bizimle paylaşabilirsiniz. Bizim
                  Kahramanlar ile ilgili sorularınız için <a href="tel:08503351358" className="text-blue">0850 335 1358</a> numaralı{" "}
                  <span className="text-blue">Connection Çağrı Merkezi'mizi</span> arayarak hafta içi 09.00-18.00 saatleri
                  arasında bize ulaşabilirsiniz.
                </p>
              </div>
            </div>
          </form>
        )}
      </Formik>

      <h5 className="text-blue mb-sm-5 mb-4">Tüm Mesajlarım</h5>
      {pageState?.responseMessages?.content?.length > 0 ? (
        <div className="accordion accordion-flush shadow-lg rounded-3 overflow-hidden mb-5">
          {pageState.responseMessages?.content?.map((item, i) => (
            <div className="accordion-item" key={i}>
              <h2 className="accordion-header">
                <button
                  className="accordion-button collapsed lh-lg d-flex"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse${i}`}
                  onClick={(event) => handleMessageClick(event, item.id)}
                >
                  <span className="small opacity-75">
                    {moment(item.dateBeginString).format("DD.MM.YYYY HH:mm")}
                  </span>
                  <span className="ms-4">{item.message}</span>
                  {item.unReadListMessages ? (
                    <div className="bg-orange text-white px-3 py-0 ms-3 rounded-pill small">
                      {item.unReadListMessages.length}
                    </div>
                  ) : null}
                </button>
              </h2>
              <div
                className="accordion-collapse collapse"
                data-bs-parent=".accordion"
                id={`collapse${i}`}
              >
                <div className="accordion-body fw-light p-4 d-flex flex-column">
                  {item.contactMessageDTOList?.length > 0 &&
                    item.contactMessageDTOList?.map((message, j) => (
                      <React.Fragment key={j}>
                        <div
                          data-test={employeeName}
                          data-test-2={message.senderName}
                          className={`d-flex ${
                            message.senderName === employeeName
                              ? "justify-content-end"
                              : "justify-content-start"
                          } mb-2`}
                        >
                          <div
                            className={`px-3 py-2 rounded-3 ${
                              message.senderName === employeeName
                                ? "bg-gray-lighter text-black"
                                : "text-white"
                            }`}
                            style={
                              loginSettings?.blue
                                ? {
                                    minWidth: "240px",
                                    maxWidth: "240px",
                                    width: "100%",
                                    backgroundColor: loginSettings?.blue,
                                  }
                                : {
                                    minWidth: "240px",
                                    maxWidth: "240px",
                                    width: "100%",
                                    backgroundColor: "#ccc",
                                  }
                            }
                          >
                            {message.icon ? (
                              <div className="my-2">
                                <img
                                  src={`${process.env.REACT_APP_IMAGES_BASE_URL}/${message.icon}`}
                                  alt=""
                                  className="rounded-3"
                                  style={{ maxWidth: "100%" }}
                                />
                              </div>
                            ) : null}
                            {message.message}

                            <div
                              className="mt-2 text-end opacity-50"
                              style={{ fontSize: "12px" }}
                            >
                              {moment(message.createDate).format("DD.MM.YYYY HH:mm")}
                            </div>

                            {message?.contentUrlList?.length > 0 &&
                              message?.contentUrlList.map(
                                (image, imageIndex) => {
                                  return (
                                    <a
                                      href={image}
                                      key={imageIndex}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mb-4"
                                      data-fancybox
                                    >
                                      <img
                                        src={image}
                                        alt=""
                                        className="img-fluid mt-3"
                                        style={{ maxWidth: "208px" }}
                                      />
                                    </a>
                                  );
                                }
                              )}
                          </div>
                        </div>
                      </React.Fragment>
                    ))}

                  {item.contactStatu === true ? (
                    <div className="bg-gray-lightest rounded-3 mt-3 p-4">
                      <h5 className="mb-4 text-blue">Cevapla</h5>
                      <Formik
                        validationSchema={replySchema}
                        onSubmit={(values, { resetForm }) =>
                          handleNewMessageByIdSubmit(
                            values,
                            { resetForm },
                            item.id,
                            i
                          )
                        }
                        initialValues={{
                          messageMain: "",
                        }}
                      >
                        {({
                          values,
                          errors,
                          touched,
                          handleChange,
                          handleBlur,
                          handleSubmit,
                        }) => (
                          <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                              <textarea
                                className={`form-control ${
                                  errors.messageMain && touched.messageMain
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Mesajınız"
                                style={{ height: "100px" }}
                                name="messageMain"
                                maxLength="1000"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.messageMain}
                              ></textarea>
                              {errors.messageMain && touched.messageMain && (
                                <div className="invalid-feedback">
                                  {errors.messageMain}
                                </div>
                              )}
                            </div>
                            <div className="mb-4 d-flex align-items-center">
                              <label className="form-label mb-0 me-4 text-nowrap small">
                                <strong>Dosya / Fotoğraf Ekleyin</strong>
                              </label>
                              <input
                                className="form-control"
                                type="file"
                                name="imgSrcMain"
                                ref={(el) => (uploadInputRefs.current[i] = el)}
                              />
                            </div>
                            <button
                              type="submit"
                              className="btn btn-blue py-sm-3 py-2 px-sm-4 px-3 w-100"
                              disabled={pageState.isLoading}
                            >
                              {pageState.isLoading ? (
                                <span className="spinner-border spinner-border-sm me-3"></span>
                              ) : null}
                              Gönder
                            </button>
                          </form>
                        )}
                      </Formik>
                    </div>
                  ) : (
                    <div className="text-center small py-4 text-red">
                      Bu mesajınız kapatılmıştır. Yeni bir mesaj başlatarak bize
                      ulaşabilirsiniz.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center">
          <h2 className="mb-4 opacity-75">Hiç Mesajınız yok</h2>
        </div>
      )}
    </React.Fragment>
  );
};

export default Messages;
